import { t } from "@lingui/core/macro";
import { DropItem } from "react-aria";
import { Level } from "~level/level.model";
import { MenuItems } from "./graphics.model";
import { MargaretPoint, Point } from "../point/point.model";
import { Alarm } from "../alarm/alarm.model";
import { ALLOWED_GRAPHICS_EXTENSIONS } from "../utils/consts";

export const isExternalIdIncluded = (pointId: string) => pointId.includes(".");

export function formatScadaGraphicPointId(
  deviceExternalId: string,
  pointName: string,
): string {
  if (!deviceExternalId) {
    throw new Error(`Point "${pointName}" is missing deviceId`);
  }

  return `${deviceExternalId}.${pointName}`;
}

export const findLevelById = (level: Level, id: number): Level | null => {
  if (level.id === id) return level;
  return level.parent ? findLevelById(level.parent, id) : null;
};

export const getBuildingIds = (level): number[] => {
  const levelIds = [level.id];

  if (level.parent) {
    const parentIds = getBuildingIds(level.parent);
    const buildingIds = parentIds.filter(
      (parentLevelId) => findLevelById(level, +parentLevelId)?.isBuilding,
    );
    levelIds.push(...buildingIds);
  }

  return levelIds;
};

export type DeviceGraphicResult = {
  deviceId: number;
  graphicId: number;
  crumbs: string[] | null;
};

export const searchInGraphicItems = ({
  items,
  searchPath,
  currentPath = [],
}: {
  items: MenuItems[];
  searchPath: string;
  currentPath?: string[];
}): string[] | null => {
  for (const item of items) {
    const crumbs =
      item.path === searchPath
        ? [...currentPath, item.name]
        : searchInGraphicItems({
            items: item.items || [],
            searchPath,
            currentPath: [...currentPath, item.name],
          });

    if (crumbs) {
      return crumbs;
    }
  }
  return currentPath;
};

/** Flatting all parent objects in given level to list */
export const flattenLevelParentToList = (level: Level | null): Level[] => {
  if (!level) return [];

  let currentLevel: Level = level;
  const list: Level[] = [];

  while (currentLevel.parent) {
    list.push(currentLevel);
    currentLevel = currentLevel.parent;
  }
  list.push(currentLevel);
  return list;
};

/** Getting first path object from items in graphics in a recursive manner. For example input=
[{name: "Backgrounds", items: [{ name: "Background",path: "", items: [{ name: "640", path: "background640.htm",items: []}] }]},{ ... }]; */
export const firstPathAndNamesInGraphicItems = (
  graphicItems: MenuItems[],
  names: string[] = [],
): { path: string; names: string[] } => {
  for (const item of graphicItems) {
    if (item.path) {
      return { path: item.path, names: [...names, item.name] };
    }
    return firstPathAndNamesInGraphicItems(item.items, [...names, item.name]);
  }
  return { path: "", names: [] };
};

// Convert Point[] to MargaretPoint[], including control/limits expansions
export function convertToMargaretPoint(
  points: (Point | Alarm)[],
): MargaretPoint[] {
  const margaretValues: MargaretPoint[] = [];

  points
    .filter((p) => p.value)
    .forEach((point) => {
      const value = point.value;
      const baseValue: MargaretPoint = {
        unit: point.unit ?? "",
        timestamp: value?.timestamp.toISO() ?? "",
        deviceId: point.device.externalId,
        pointName: point.name,
        value: value?.value ?? null,
        decimals: point.decimals ?? null,
        state: value?.label ?? null,
        lockState: value?.lockState ?? null,
        alarmState: value && "alarmState" in value ? value.alarmState : null,
        errorState: value?.errorState ?? null,
        deviceState: value?.deviceState ?? null,
        extension: value?.extension ?? null,
        description: point.description,
      };

      const extension = point.value!.extension;
      // Process extension.control
      if (extension?.control) {
        // Control point extension values has each own unit
        const extensionUnits = (point as Point).extension?.units;
        const paramMap = extension.control!;
        Object.keys(extension.control).forEach((paramId) => {
          const pointName = `${baseValue.pointName}#CONTROL#${paramId}`;

          margaretValues.push({
            ...baseValue,
            pointName,
            lockState: value?.lockState ?? null,
            value: paramMap[paramId],
            unit: extensionUnits ? (extensionUnits[paramId] ?? "") : "",
          });
        });
      }

      // Process extension.limits
      if (extension?.limits) {
        extension.limits.forEach((limit) => {
          margaretValues.push({
            ...baseValue,
            ...limit,
            pointName: `${baseValue.pointName}:${limit.index}`,
          });
        });
      }
      margaretValues.push(baseValue);
    });
  return margaretValues;
}

const getFileExtension = (filename: string) => {
  const parts = filename.split(".");
  const extension = parts.length > 1 ? parts[parts.length - 1] : null;
  return extension;
};

export const validateScadaFiles = (files: File[]): string | null => {
  if (files.length === 0) return null;

  const invalidFiles: string[] = [];

  for (const file of files) {
    const extension = getFileExtension(file.name);

    if (
      !extension ||
      !ALLOWED_GRAPHICS_EXTENSIONS.includes(extension.toLowerCase())
    ) {
      invalidFiles.push(file.name);
    }
  }

  if (invalidFiles.length > 0) {
    return t`The folder contains the following invalid files: ${invalidFiles.join(", ")}. Please remove the files and reload the folder.`;
  }

  return null;
};

// Recursively read directory entries
export const readDirectoryItem = async (
  item: DropItem,
  basePath = "",
): Promise<File[]> => {
  const allFiles: File[] = [];

  if (item.kind === "file") {
    const file = await item.getFile();
    const relativePath = basePath ? `${basePath}/${file.name}` : file.name;

    Object.defineProperty(file, "webkitRelativePath", {
      value: relativePath,
      writable: false,
    });

    allFiles.push(file);
  } else if (item.kind === "directory") {
    for await (const entry of item.getEntries()) {
      const subPath = basePath ? `${basePath}/${item.name}` : item.name;
      const subFiles = await readDirectoryItem(entry, subPath);
      allFiles.push(...subFiles);
    }
  }

  return allFiles;
};

/**
 * Parses a point name and an optional numeric limit extension.
 *
 * If the point name ends with `:<number>`, the numeric part is treated as the
 * limit and removed from the returned point name.
 *
 * If the text after the colon is not purely numeric, the colon is considered
 * part of the point name.
 *
 * Examples:
 * - "TESTI_DEMO:2"        -> { pointBaseName: "TESTI_DEMO", limit: "2" }
 * - "TESTI_DEMO:2_TEST" -> { pointBaseName: "TESTI_DEMO:2_TEST", limit: undefined }
 */
export function parsePointName(rawPointName: string): {
  pointBaseName: string;
  limit: string | undefined;
} {
  const match = rawPointName?.match(/^(.*):(\d+)$/);

  return match
    ? { pointBaseName: match[1], limit: match[2] }
    : { pointBaseName: rawPointName, limit: undefined };
}
